import { useState } from 'react';
import { Monitor, FileText, X } from 'lucide-react';

interface RenderedEmail {
  subject: string;
  html: string;
  text: string;
}

interface PreviewPanelProps {
  rendered: RenderedEmail;
}

export function PreviewPanel({ rendered }: PreviewPanelProps) {
  const [viewMode, setViewMode] = useState<'html' | 'text'>('html');
  const [isExpanded, setIsExpanded] = useState(true);

  if (!isExpanded) {
    return (
      <div className="border rounded-lg p-4 bg-muted/20">
        <button
          onClick={() => setIsExpanded(true)}
          className="text-sm text-primary hover:underline"
        >
          Show preview
        </button>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      <div className="flex items-center justify-between p-4 border-b bg-muted/30">
        <div>
          <h3 className="font-semibold">Email Preview</h3>
          <p className="text-sm text-muted-foreground">
            Preview with sample data
          </p>
        </div>
        <button
          onClick={() => setIsExpanded(false)}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Subject Line */}
      <div className="p-4 bg-muted/10 border-b">
        <div className="text-xs font-medium text-muted-foreground mb-1">Subject:</div>
        <div className="font-medium">{rendered.subject}</div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex border-b bg-muted/30">
        <button
          onClick={() => setViewMode('html')}
          className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors ${
            viewMode === 'html'
              ? 'bg-background border-b-2 border-primary text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Monitor className="h-4 w-4" />
          HTML Preview
        </button>
        <button
          onClick={() => setViewMode('text')}
          className={`flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors ${
            viewMode === 'text'
              ? 'bg-background border-b-2 border-primary text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <FileText className="h-4 w-4" />
          Plain Text
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {viewMode === 'html' ? (
          <div className="border rounded-lg overflow-hidden bg-white">
            <iframe
              srcDoc={rendered.html}
              title="Email Preview"
              className="w-full h-[600px] border-0"
              sandbox="allow-same-origin"
            />
          </div>
        ) : (
          <div className="border rounded-lg p-4 bg-muted/10">
            <pre className="whitespace-pre-wrap font-mono text-sm text-foreground">
              {rendered.text}
            </pre>
          </div>
        )}
      </div>

      {/* Info footer */}
      <div className="p-3 bg-muted/20 border-t text-xs text-muted-foreground">
        Preview uses sample data. Actual emails will use real data when sent.
      </div>
    </div>
  );
}
